import type { AuthFormData } from '../types/auth'
import { User } from '../types/ffe'
import { apiClient } from './apiClient'
import { Organisation, OrganisationData } from './ApiContext'
import { getCountriesForTimezone } from 'countries-and-timezones'

export const authApi = {
  async login(data: AuthFormData) {
    const response = await apiClient.request<{
      accessToken: string
      authentication: unknown
      user: User
    }>('/authentication', {
      method: 'POST',
      body: JSON.stringify({
        strategy: 'local',
        email: data.email,
        password: data.password,
        token: data.captcha
      }),
      skipAuthError: true
    })

    if (response.accessToken) {
      apiClient.setToken(response.accessToken)
      apiClient.setIsSignedUp(!response.user.isDemo)
    }

    return response
  },

  async loginAsUser(email: string) {
    const response = await apiClient.request<{
      accessToken: string
      authentication: unknown
      user: User
    }>('/authentication', {
      method: 'POST',
      body: JSON.stringify({
        strategy: 'admin',
        email: email,
        accessToken: localStorage.getItem('auth_token')
      }),
      skipAuthError: true
    })

    if (response.accessToken) {
      apiClient.setToken(response.accessToken)
      apiClient.setIsSignedUp(!response.user.isDemo)
    }

    return response
  },

  async convertDemoToUser(data: AuthFormData, user: User) {
    if (!data.name) {
      data.name = ''
    }
    const splitName = data.name.split(' ')
    const firstName = splitName.slice(0, -1).join(' ')
    const lastName = splitName[splitName.length - 1]
    const newUser = await apiClient.patch<User>('/users/' + user._id, {
      isDemo: false,
      firstName: firstName,
      lastName: lastName,
      email: data.email,
      password: data.password
    })
    const org = await apiClient.patch<Organisation>(
      '/organisations/' + newUser.organisationId,
      {
        name: data.company
      }
    )
    apiClient.setIsSignedUp(true)

    return { user: newUser, company: org }
  },

  async reAuthenticate() {
    const response = await apiClient.request<{
      accessToken: string
      authentication: unknown
      user: User
    }>('/authentication', {
      method: 'POST',
      body: JSON.stringify({
        strategy: 'jwt',
        accessToken: localStorage.getItem('auth_token')
      })
    })

    if (response.accessToken) {
      apiClient.setToken(response.accessToken)
      apiClient.setIsSignedUp(!response.user.isDemo)
    }

    return response
  },

  async signup(
    data: AuthFormData,
    organisationDataFromStore: OrganisationData
  ) {
    if (!data.name) {
      data.name = ''
    }
    const splitName = data.name.split(' ')
    const firstName = splitName.slice(0, -1).join(' ')
    const lastName = splitName[splitName.length - 1]
    const userResponse = await apiClient.post<User>('/users', {
      email: data.email,
      password: data.password,
      firstName: firstName,
      lastName: lastName,
      token: data.captcha,
      isDemo: data.isDemo
    })
    await this.login(data)
    const responseCompany = await apiClient.post<
      Organisation,
      OrganisationData
    >('/organisations', {
      name: organisationDataFromStore.name,
      address: organisationDataFromStore.address,
      phone: organisationDataFromStore.phone,
      email: organisationDataFromStore.email,
      logo: organisationDataFromStore.logo,
      primaryColor: organisationDataFromStore.primaryColor,
      defaultMargin: organisationDataFromStore.defaultMargin,
      taxRate: organisationDataFromStore.taxRate,
      country:
        getCountriesForTimezone(
          Intl.DateTimeFormat().resolvedOptions().timeZone
        )[0]?.id ?? 'NZ',
      taxNumber: organisationDataFromStore.taxNumber,
      projectSettings: {
        defaultHeader: organisationDataFromStore.projectSettings?.defaultHeader,
        defaultFooter: organisationDataFromStore.projectSettings?.defaultFooter
      },
    })

    return { user: userResponse, company: responseCompany }
  },

  async logout() {
    apiClient.setToken(null)
    apiClient.setIsSignedUp(false)
  }
}
